import type { Cue } from '../core/model/cue.ts';
import type { TrackList } from '../core/model/track.ts';

export type SubtitleSlot = 'main' | 'second';
export type RawTrackType = 'text' | 'image';

export type SlotViewState = {
  delayMs: number;
  hidden: boolean;
  dualMode: boolean;
  priority?: Cue[];
};

export type SubtitleSlotState = {
  trackList?: TrackList;
  selectedId?: string;
  cues?: Cue[];
  rawType?: RawTrackType;
  imageExtent?: { h: number; v: number } | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  view: SlotViewState;
};

export type SubtitleState = Record<SubtitleSlot, SubtitleSlotState>;
export type StoreListener = (state: SubtitleState) => void;

function createSlotState(): SubtitleSlotState {
  return {
    status: 'idle',
    view: { delayMs: 0, hidden: false, dualMode: false },
  };
}

function extentEqual(
  a: { h: number; v: number } | null | undefined,
  b: { h: number; v: number } | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a == null && b == null;
  return a.h === b.h && a.v === b.v;
}

export class SubtitleStore {
  private state: SubtitleState = {
    main: createSlotState(),
    second: createSlotState(),
  };

  private readonly listeners = new Set<StoreListener>();

  getState(): SubtitleState {
    return this.state;
  }

  getSlot(slot: SubtitleSlot): SubtitleSlotState {
    return this.state[slot];
  }

  setTrackList(slot: SubtitleSlot, trackList: TrackList): void {
    this.patchSlot(slot, { trackList });
  }

  select(slot: SubtitleSlot, selectedId: string | undefined): void {
    this.patchSlot(slot, { selectedId });
  }

  setCues(
    slot: SubtitleSlot,
    cues: Cue[] | undefined,
    track: { type?: RawTrackType; extent?: { h: number; v: number } | null } = {},
  ): void {
    const prev = this.state[slot];
    const nextStatus: SubtitleSlotState['status'] = cues === undefined ? 'idle' : 'ready';
    const nextType = track.type;
    const nextExtent = track.extent;
    // Same array reference + meta → no notify (in-place cue field edits still need
    // RenderController.invalidate / playing rAF to pick up via signature).
    if (
      prev.cues === cues
      && prev.rawType === nextType
      && extentEqual(prev.imageExtent, nextExtent)
      && prev.status === nextStatus
    ) {
      return;
    }
    this.patchSlot(slot, {
      cues,
      rawType: nextType,
      imageExtent: nextExtent,
      status: nextStatus,
    });
  }

  setStatus(slot: SubtitleSlot, status: SubtitleSlotState['status']): void {
    this.patchSlot(slot, { status });
  }

  setViewState(slot: SubtitleSlot, patch: Partial<SlotViewState>): void {
    const prev = this.state[slot].view;
    const next: SlotViewState = { ...prev, ...patch };
    if (
      prev.delayMs === next.delayMs
      && prev.hidden === next.hidden
      && prev.dualMode === next.dualMode
      && prev.priority === next.priority
    ) {
      return;
    }
    this.patchSlot(slot, { view: next });
  }

  reset(): void {
    this.state = { main: createSlotState(), second: createSlotState() };
    this.emit();
  }

  subscribe(listener: StoreListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private patchSlot(slot: SubtitleSlot, patch: Partial<SubtitleSlotState>): void {
    this.state = {
      ...this.state,
      [slot]: { ...this.state[slot], ...patch },
    };
    this.emit();
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener(this.state));
  }
}
